import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress, TableContainer, useTheme,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Inspection } from '../services/api';

const STATUS_COLOR = (theme: Theme): Record<string, { bg: string; text: string }> => ({
  pending: { bg: alpha(theme.palette.text.primary, 0.06), text: 'text.secondary' },
  in_progress: { bg: 'warning.dark', text: 'warning.main' },
  completed: { bg: 'success.dark', text: 'success.main' },
  reviewed: { bg: 'secondary.dark', text: 'secondary.main' },
});

export default function InspectionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project') ?? undefined;
  const theme = useTheme();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setInspections(await api.getInspections(projectId)); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
          Inspections {projectId ? '(filtered by project)' : ''}
        </Typography>
        <Button
          variant="outlined"
          onClick={load}
        >
          Refresh
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['Title', 'Status', 'Defects', 'Date', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 12, fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {inspections.map(insp => {
                const statusKey = insp.status as keyof ReturnType<typeof STATUS_COLOR>;
                const sc = STATUS_COLOR(theme)[statusKey] ?? STATUS_COLOR(theme).pending;
                return (
                  <TableRow key={insp.id} hover sx={{ '&:hover': { bgcolor: 'background.default' }, cursor: 'pointer' }}>
                    <TableCell sx={{ color: 'text.primary', borderColor: 'divider' }} onClick={() => navigate(`/report/${insp.id}`)}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{insp.title}</Typography>
                      {insp.location_description && (
                        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>📍 {insp.location_description}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderColor: 'divider' }}>
                      <Chip label={insp.status.replace('_', ' ')} size="small" sx={{ bgcolor: sc.bg, color: sc.text, fontSize: 11 }} />
                    </TableCell>
                    <TableCell sx={{ color: insp.defect_count > 0 ? 'error.light' : 'text.secondary', borderColor: 'divider' }}>
                      {insp.defect_count}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 12 }}>
                      {new Date(insp.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ borderColor: 'divider' }}>
                      <Button
                        size="small"
                        onClick={() => navigate(`/report/${insp.id}`)}
                        sx={{ color: 'primary.main', fontSize: 12, mr: 1 }}>
                        Report
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {inspections.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary', borderColor: 'divider', py: 6 }}>
                    No inspections found. Start a scan from the mobile app or glasses.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
